/**
 * Search — the funnel readout: the starting group + one bar per step (people
 * kept), with the no-answer count and the first warning under each. Pure
 * display; step clicks bubble up (drill-in).
 *
 * Bars are labelled by step_id, never by position: the definition sent to the
 * server drops empty steps, so the stats and the step list can be different
 * lengths and an index lookup would put one step's words on another's bar.
 * The same goes for the bar's number: it is the step's place in the full
 * definition, so it matches the number on the step's card.
 * Question words for definitions saved without them are looked up here, so
 * every page that renders the rail reads in sentences — unless the page hands
 * its own lookup in through ctx.questionLabels (the builder and the run page
 * both do, for the same keys), when asking again would only be a second
 * request for words the page already has.
 */
import { useMemo } from 'react';
import { AlertTriangle } from 'lucide-react';
import BarRow from '../reports/BarRow';
import { SummaryStat } from '../reports/StatBlocks';
import type { SearchStepStats, SearchStepDef } from '../../types';
import {
  describeStep, isQuestionStep, questionKeysOf, stepNumber, unknownWords, useQuestionLabels, warningSummary,
  type DescribeCtx,
} from '../../pages/search/searchLib';

interface Props {
  baseTotal: number | null;
  steps: SearchStepStats[];
  stepDefs?: SearchStepDef[];
  finalTotal: number | null;
  onStepClick?: (stepId: string) => void;
  activeStepId?: string | null;
  ctx?: DescribeCtx;
}

export default function FunnelRail({ baseTotal, steps, stepDefs, finalTotal, onStepClick, activeStepId, ctx }: Props) {
  const max = Math.max(1, baseTotal ?? 0);
  const pct = baseTotal && finalTotal !== null && baseTotal > 0
    ? `${((finalTotal / baseTotal) * 100).toFixed(1)}%`
    : '—';

  const fetched = useQuestionLabels(ctx?.questionLabels ? [] : questionKeysOf({ steps: stepDefs }));
  const labelCtx = useMemo<DescribeCtx>(
    () => ({ ...(ctx || {}), questionLabels: { ...fetched, ...(ctx?.questionLabels || {}) } }),
    [ctx, fetched],
  );
  const defById = useMemo(() => new Map((stepDefs || []).map((d) => [d.id, d])), [stepDefs]);

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-3 gap-2">
        <SummaryStat label="Starting group" value={baseTotal === null ? '—' : baseTotal.toLocaleString()} />
        <SummaryStat label="Final list" value={finalTotal === null ? '—' : finalTotal.toLocaleString()} highlight />
        <SummaryStat label="Of the starting group" value={pct} />
      </div>
      <div className="rounded-xl border border-gray-200 bg-white p-2 dark:border-gray-700 dark:bg-gray-800">
        <BarRow label="Starting group" count={baseTotal ?? 0} max={max} color="bg-gray-300 dark:bg-gray-600" />
        {steps.map((s, i) => {
          const def = defById.get(s.step_id);
          const question = isQuestionStep(def);
          const warn = warningSummary(s, question);
          // Numbered like the step's card: the stats skip unfinished steps (they aren't sent), so the
          // stats index would call the builder's card 2 "1." here.
          const n = stepNumber(s.step_id, stepDefs, i);
          return (
            <div key={s.step_id}>
              <BarRow
                label={`${n}. ${def ? describeStep(def, labelCtx) : s.label || `Step ${n}`}`}
                count={s.passing}
                max={max}
                hint={s.no_data > 0 ? `${s.no_data.toLocaleString()} ${unknownWords(question).count}` : null}
                color={s.mode === 'exclude' ? 'bg-rose-400' : 'bg-primary-500'}
                onClick={onStepClick ? () => onStepClick(s.step_id) : undefined}
                active={activeStepId === s.step_id}
              />
              {warn && (
                <div className="flex items-start gap-1 px-2 pb-1 text-xs text-amber-600 dark:text-amber-400">
                  <AlertTriangle className="mt-0.5 h-3 w-3 flex-shrink-0" />
                  <span>{warn.first}{warn.more > 0 ? ` (and ${warn.more} more)` : ''}</span>
                </div>
              )}
            </div>
          );
        })}
        {steps.length === 0 && (
          <p className="px-2 py-3 text-sm text-gray-400 dark:text-gray-500">
            Add steps to narrow the starting group — each step shows who it kept, who it dropped, and who has no answer on file.
          </p>
        )}
      </div>
    </div>
  );
}
